import { Box, Typography, Skeleton } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import '@fontsource/rubik';

const CustomTooltipContent = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0];
  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #E9EBE9',
        borderRadius: '8px',
        px: 1.5,
        py: 1,
        boxShadow: '0 4px 12px rgba(8,35,27,0.12)',
        fontFamily: 'Rubik',
        opacity: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '2px',
            backgroundColor: entry.payload.color,
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontFamily: 'Rubik', fontSize: 12, color: '#08231B' }}>
          {entry.name}: <strong>{entry.value}</strong>
        </Typography>
      </Box>
    </Box>
  );
};

export default function VideoTypesDonut({ videoTypes, isLoading }) {
  const types = videoTypes?.video_types || [];
  const total = videoTypes?.total ?? 0;
  const periodLabel = videoTypes?.period_label || 'last 6 months';

  const chartData = types.map((t) => ({
    name: t.name,
    value: t.count,
    color: t.color,
  }));

  return (
    <Box
      id="cd-video-types-chart"
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #E9EBE9',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(8,35,27,0.04)',
      }}
    >
      {/* Header */}
      <Box sx={{ px: '20px', pt: '18px', pb: '4px' }}>
        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: { xs: '14px', lg: '15px', xl: '16px' },
            fontWeight: 700,
            color: '#08231B',
          }}
        >
          Video Types
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: { xs: '11px', lg: '12px', xl: '13px' },
            color: '#6b7470',
            mt: 0.25,
          }}
        >
          {total} videos · {periodLabel}
        </Typography>
      </Box>

      {/* Donut chart */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          px: '20px',
          pb: '20px',
          pt: '8px',
        }}
      >
        {isLoading ? (
          <Skeleton variant="circular" width={160} height={160} />
        ) : (
          <Box sx={{ position: 'relative', width: 160, height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={72}
                  paddingAngle={2}
                  dataKey="value"
                  strokeWidth={0}
                  startAngle={90}
                  endAngle={-270}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomTooltipContent />}
                  wrapperStyle={{ zIndex: 10, outline: 'none' }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: '28px',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                  color: '#08231B',
                }}
              >
                {total}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  color: '#6b7470',
                  textTransform: 'uppercase',
                  mt: 0.25,
                }}
              >
                Videos
              </Typography>
            </Box>
          </Box>
        )}

        {/* Type list */}
        <Box sx={{ width: '100%' }}>
          {isLoading
            ? [1, 2, 3, 4].map((k) => (
                <Box
                  key={k}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '12px 1fr auto',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    borderTop: k === 1 ? 'none' : '1px solid #E9EBE9',
                  }}
                >
                  <Skeleton variant="rectangular" width={10} height={10} sx={{ borderRadius: '2px' }} />
                  <Skeleton width="70%" height={14} />
                  <Skeleton width={20} height={14} />
                </Box>
              ))
            : types.map((t, i) => (
                <Box
                  key={t.name}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '12px 1fr auto',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    borderTop: i === 0 ? 'none' : '1px solid #E9EBE9',
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '2px',
                      backgroundColor: t.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Rubik',
                      fontSize: { xs: '11px', lg: '12px', xl: '13px' },
                      color: '#282831',
                    }}
                  >
                    {t.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Rubik',
                      fontSize: { xs: '12px', lg: '13px', xl: '14px' },
                      fontWeight: 600,
                      color: '#08231B',
                    }}
                  >
                    {t.count}
                  </Typography>
                </Box>
              ))}
        </Box>
      </Box>
    </Box>
  );
}
